import { useMemo, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { FormField } from '@/components/form';
import {
  PROPERTY_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_MAX_BYTES,
} from '@/lib/constants/propertyMedia';
import {
  isAllowedPropertyMediaType,
  isWithinPropertyMediaSizeLimit,
  parsePropertyMediaUrl,
  removePropertyMediaFiles,
  uploadPropertyMediaFile,
} from '@/lib/storage/propertyMedia';

interface UnitHeroImageFieldProps {
  propertyId: string;
  heroImageUrl: string;
  disabled: boolean;
  error?: string;
  onHeroImageUrlChange: (next: string) => void;
  onUploadingChange: (uploading: boolean) => void;
}

export function UnitHeroImageField({
  propertyId,
  heroImageUrl,
  disabled,
  error,
  onHeroImageUrlChange,
  onUploadingChange,
}: UnitHeroImageFieldProps): JSX.Element {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const maxImageSizeGb = useMemo(
    () => Math.round(PROPERTY_MEDIA_MAX_BYTES / (1024 * 1024 * 1024)),
    [],
  );
  const acceptTypes = useMemo(() => PROPERTY_MEDIA_ALLOWED_TYPES.join(','), []);

  const setUploading = (next: boolean) => {
    setIsUploading(next);
    onUploadingChange(next);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []).slice(0, 1);
    if (files.length === 0) {
      return;
    }

    setUploadError(null);

    const [file] = files;
    if (!isAllowedPropertyMediaType(file)) {
      setUploadError(`Unsupported file type: ${file.type || file.name}.`);
      event.target.value = '';
      return;
    }

    if (!isWithinPropertyMediaSizeLimit(file)) {
      setUploadError(`Each image must be under ${maxImageSizeGb.toString()}GB.`);
      event.target.value = '';
      return;
    }

    setUploading(true);

    try {
      if (heroImageUrl) {
        const parsed = parsePropertyMediaUrl(heroImageUrl);
        if (parsed) {
          await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
        }
      }

      const result = await uploadPropertyMediaFile({ propertyId, file });
      if (typeof result.url === 'string' && result.url.length > 0) {
        onHeroImageUrlChange(result.url);
        toast.success('Uploaded hero image.');
      }
    } catch (uploadError) {
      const message =
        uploadError instanceof Error ? uploadError.message : 'Upload failed. Please try again.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = async (): Promise<void> => {
    if (!heroImageUrl) {
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const parsed = parsePropertyMediaUrl(heroImageUrl);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      onHeroImageUrlChange('');
      toast.success('Removed hero image.');
    } catch (removeError) {
      const message =
        removeError instanceof Error ? removeError.message : 'Unable to remove image from storage.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const canInteract = !disabled && !isUploading;

  return (
    <FormField
      id="unit-hero-image"
      label="Hero image"
      description={`Optional. Shown on public unit cards and the unit detail hero. Accepted types: JPG, PNG, WebP, HEIC. Max ${maxImageSizeGb.toString()}GB.`}
      error={uploadError ?? error}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex h-28 w-40 items-center justify-center overflow-hidden rounded-xl border border-surface-subtle bg-surface-subtle/60">
            {heroImageUrl ? (
              <img src={heroImageUrl} alt="Unit hero" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full" aria-hidden="true" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5">
              <input
                type="file"
                accept={acceptTypes}
                className="sr-only"
                onChange={handleFileChange}
                disabled={!canInteract}
              />
              {isUploading ? 'Uploading…' : heroImageUrl ? 'Replace image' : 'Upload image'}
            </label>
            {heroImageUrl ? (
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={!canInteract}
                className="text-sm font-semibold text-red-700 underline-offset-4 transition hover:underline disabled:opacity-60"
              >
                Remove image
              </button>
            ) : null}
            <p className="text-xs text-text-primary/70">
              Images are stored with the property and reused across layouts.
            </p>
          </div>
        </div>
      </div>
    </FormField>
  );
}
